import React, { useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { WithdrawalAmountField } from './WithdrawalAmountField';

import { OperationStatusType } from '@/features/Modals/ui/OperationStatusModal';
import { Store } from '@/shared/store';
import { useModal, useWithdrawalLpToken } from '@/features/Modals/hooks';
import { ModalName } from '@/shared/store/modals/constants';
import { showModalAction } from '@/shared/store/modals/actions';
import { formatWithCommas, formatWithMaxPrecision } from '@/shared/utils/helpers';
import WithdrawalModalSchema, {
    WithdrawalModalData,
} from '@/shared/validations/withdrawalModal.schema';
import { Button, Text, TipCard, TooltipWrapper } from '@/shared/ui';
import { SummaryRow } from '@/widgets/Modals';
import { useGetPoolUtilisation } from '@/features/Modals/hooks/useGetPoolUtilisation';

const WithdrawalModalTexts = {
    title: 'How much do you want to withdraw?',
    tip: 'Arkis Liquidity (LP) tokens automatically earn interest proportional to your share of the pool.',
    tooltips: {
        poolUtilization: 'Total token supply being used by borrowers for their operations',
        poolBalance: 'Pool Balance shows the available tokens that have not been used by borrowers',
    },
};

enum WithdrawalModalFields {
    AMOUNT = 'amount',
}

export const PoolsWithdrawalV2Modal = () => {
    const { modalProps } = useModal();
    const dispatch = useDispatch();
    const walletAddress = useSelector((state: Store) => state.wallets.active.address);
    const {
        tokenSymbol,
        currentApy,
        tokenAddress,
        totalPosition,
        totalDepositThreshold,
        decimals,
        agreementAddress,
        poolBalance,
        refresh,
    } = modalProps || {};

    const methods = useForm<WithdrawalModalData>({
        resolver: yupResolver(WithdrawalModalSchema),
        reValidateMode: 'onChange',
    });

    const poolUtilization = useGetPoolUtilisation();

    const { makeWithdrawal } = useWithdrawalLpToken();

    const onSubmit = async (data: WithdrawalModalData) => {
        if (!walletAddress) {
            dispatch(
                showModalAction({
                    modalName: ModalName.CONNECT_WALLET_MODAL,
                    modalTitle: 'Connect Wallet',
                }),
            );
            return;
        }

        dispatch(
            showModalAction({
                modalName: ModalName.OPERATION_STATUS_MODAL,
                modalProps: {
                    type: OperationStatusType.Pending,
                },
            }),
        );

        await makeWithdrawal({
            amount: data[WithdrawalModalFields.AMOUNT],
            agreementAddress,
            tokenAddress,
            totalPosition,
            poolBalance,
            decimals,
        });

        if (refresh) refresh();
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} className={'w-[448px] pt-7'}>
                <Text className='mb-3 font-medium' name='withdrawal-modal-title'>
                    {WithdrawalModalTexts.title}
                </Text>
                <WithdrawalAmountField name={WithdrawalModalFields.AMOUNT} />
                <div className='mt-7'>
                    <TipCard text={WithdrawalModalTexts.tip} title='Tip' />
                </div>
                <div className=' border-b pb-7 mb-7 mt-5 border-border-opaque'>
                    <SummaryRow label='Current APY' value={`${currentApy}%`} />

                    <SummaryRow label='Pool Utilization' value={`${poolUtilization}%`}>
                        <TooltipWrapper text={WithdrawalModalTexts.tooltips.poolUtilization} />
                    </SummaryRow>

                    <SummaryRow label='Pool Balance' className={'border-none'}>
                        <div className='flex gap-2'>
                            <Text
                                type={Text.types.Paragraph}
                                fontWeight={Text.weights.SemiBold}
                                name='withdrawal-total-deposited'
                            >{`${formatWithCommas(
                                formatWithMaxPrecision(poolBalance, decimals),
                            )} ${tokenSymbol}`}</Text>
                            <Text
                                type={Text.types.Paragraph}
                                fontWeight={Text.weights.Regular}
                                className='text-content-tertiary'
                                name='withdrawal-total-deposited-threshold'
                            >{`/ ${formatWithCommas(totalDepositThreshold)} ${tokenSymbol}`}</Text>
                        </div>
                        <TooltipWrapper text={WithdrawalModalTexts.tooltips.poolBalance} />
                    </SummaryRow>
                </div>

                <SubmitButton />
            </form>
        </FormProvider>
    );
};

function SubmitButton() {
    const walletAddress = useSelector((state: Store) => state.wallets.active.address);
    const { watch, getFieldState } = useFormContext();
    const { modalProps } = useModal();
    const { tokenSymbol, poolBalance } = modalProps || {};
    const amount = watch(WithdrawalModalFields.AMOUNT);
    const { error } = getFieldState(WithdrawalModalFields.AMOUNT) || {};

    const getSubmitButtonText = () => {
        return walletAddress ? `Withdraw ${tokenSymbol}` : 'Connect Wallet';
    };

    const isDisabled = useMemo(() => {
        if (error) return true;
        if (!amount || Number(amount) === 0) return true;
        return Number(poolBalance) < Number(amount);
    }, [poolBalance, amount, walletAddress, error]);

    return (
        <Button
            size={Button.sizes.Large}
            disabled={isDisabled}
            className='w-full font-semibold'
            type='submit'
        >
            {getSubmitButtonText()}
        </Button>
    );
}
